import type {
    DuplicateCluster,
    DuplicateClusterMember,
} from "@mrflark/spl_sdk";
import {
    Button,
} from "@mrflark/ui";
import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import {
    Check,
    Copy,
    GitMerge,
    Layers,
    ShieldOff,
} from "lucide-preact";
import {
    useMemo,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    splClient,
} from "../../api/client.ts";
import {
    MergeDuplicatesModal,
} from "./MergeDuplicatesModal.tsx";

const formatBytes = (bytes?: number): string => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso?: string): string => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

// per-cluster card. members rendered as a horizontal strip of selectable
// thumbnails so the operator can scan a cluster of 3-12 visually similar
// items in one glance. each card carries its own selection state because
// merge / exempt actions only operate on the currently-checked subset
// within that single cluster
const ClusterCard = ({
    cluster,
    onMerge,
    onExempt,
    isMerging,
    isExempting,
}: {
    cluster: DuplicateCluster;
    onMerge: (cluster: DuplicateCluster, selected: string[]) => void;
    onExempt: (cluster: DuplicateCluster, selected: string[]) => void;
    isMerging: boolean;
    isExempting: boolean;
}) => {
    // default to every member checked. the most common merge/exempt
    // operation hits the entire cluster, so making operators uncheck
    // outliers is faster than asking them to check the common case
    const [selected, setSelected] = useState<Set<string>>(
        () => new Set(cluster.members.map((m) => m.id)),
    );

    const toggle = (id: string): void => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const setAll = (): void =>
        setSelected(new Set(cluster.members.map((m) => m.id)));
    const setNone = (): void => setSelected(new Set());

    const selectedIds = [...selected];
    const canAct = selectedIds.length >= 2;

    return (
        <article className="bg-bg-secondary border border-sky-500/20 rounded-lg overflow-hidden">
            <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-sky-500/20 bg-gradient-to-r from-sky-500/5 to-transparent">
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sky-500/15 text-sky-300 text-xs font-medium">
                        <Layers size={13} />
                        Cluster of {cluster.members.length}
                    </span>
                    <span className="text-xs text-text-muted">
                        {selectedIds.length} / {cluster.members.length} selected
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={setAll}
                        className="text-[11px] uppercase tracking-wider text-text-muted hover:text-text-primary px-2 py-1 rounded hover:bg-bg-tertiary"
                    >
                        All
                    </button>
                    <button
                        type="button"
                        onClick={setNone}
                        className="text-[11px] uppercase tracking-wider text-text-muted hover:text-text-primary px-2 py-1 rounded hover:bg-bg-tertiary"
                    >
                        None
                    </button>
                    <span className="w-px h-4 bg-border mx-1" />
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onExempt(cluster, selectedIds)}
                        disabled={!canAct || isExempting}
                        title="Mark these as not duplicates of one another"
                    >
                        <ShieldOff size={14} className="mr-1" />
                        Not duplicates
                    </Button>
                    <Button
                        variant="error"
                        size="sm"
                        onClick={() => onMerge(cluster, selectedIds)}
                        disabled={!canAct || isMerging}
                        title="Merge metadata into one image and delete the rest"
                    >
                        <GitMerge size={14} className="mr-1" />
                        Merge & delete
                    </Button>
                </div>
            </header>

            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {cluster.members.map((member) => (
                    <ClusterMemberCard
                        key={member.id}
                        member={member}
                        isSelected={selected.has(member.id)}
                        onToggle={() => toggle(member.id)}
                    />
                ))}
            </div>
        </article>
    );
};

const ClusterMemberCard = ({
    member,
    isSelected,
    onToggle,
}: {
    member: DuplicateClusterMember;
    isSelected: boolean;
    onToggle: () => void;
}) => {
    return (
        <label
            className={`group relative block rounded-md overflow-hidden cursor-pointer border-2 transition-all duration-150 ${
                isSelected
                    ? "border-accent shadow-[0_0_0_3px_rgba(255,255,255,0.04)]"
                    : "border-border hover:border-accent/50 opacity-75 hover:opacity-100"
            }`}
        >
            <input
                type="checkbox"
                checked={isSelected}
                onChange={onToggle}
                className="sr-only"
                aria-label={`Select ${member.id}`}
            />
            <div className="absolute top-1.5 left-1.5 z-10">
                <span
                    className={`flex items-center justify-center w-5 h-5 rounded-md border-2 ${
                        isSelected
                            ? "bg-accent border-accent"
                            : "bg-bg-secondary/80 border-border backdrop-blur"
                    }`}
                >
                    {isSelected && <Check size={12} className="text-white" />}
                </span>
            </div>
            <button
                type="button"
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    globalThis.open(`/?id=${member.id}`, "_blank");
                }}
                className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide bg-bg-secondary/80 text-text-secondary backdrop-blur hover:bg-bg-secondary hover:text-text-primary"
                title="Open in viewer"
            >
                Open
            </button>
            <div className="aspect-square bg-bg-tertiary">
                {member.thumbnail_url
                    ? (
                        <img
                            src={member.thumbnail_url}
                            alt=""
                            className={`w-full h-full object-cover transition-all duration-200 ${
                                isSelected ? "" : "saturate-50"
                            }`}
                            loading="lazy"
                        />
                    )
                    : (
                        <div className="w-full h-full flex items-center justify-center text-text-muted text-xs">
                            no thumb
                        </div>
                    )}
            </div>
            <div className="p-2 bg-bg-secondary text-[11px] space-y-0.5">
                <div className="text-text-secondary">
                    {formatDate(member.upload_date)}
                </div>
                <div className="flex justify-between text-text-muted">
                    <span>{formatBytes(member.file_size_bytes)}</span>
                    <span>
                        {member.tags.length}t · {member.views}v
                    </span>
                </div>
            </div>
        </label>
    );
};

export const DuplicateClusterReviewView = ({
    clusters,
}: {
    clusters: DuplicateCluster[];
}) => {
    const queryClient = useQueryClient();
    const [mergeTarget, setMergeTarget] = useState<
        {
            cluster: DuplicateCluster;
            selected: string[];
        } | null
    >(null);

    const invalidate = (): void => {
        queryClient.invalidateQueries({ queryKey: ["reviewItems"] });
        queryClient.invalidateQueries({ queryKey: ["duplicateClusters"] });
    };

    const exemptMutation = useMutation({
        mutationFn: (mediaIds: string[]) =>
            splClient.exemptDuplicates(mediaIds),
        onSuccess: (response) => {
            toast.success(
                `Marked ${response.cleared_count} item${
                    response.cleared_count === 1 ? "" : "s"
                } as not duplicates (+${response.inserted_count} new exemption${
                    response.inserted_count === 1 ? "" : "s"
                })`,
            );
            invalidate();
        },
        onError: (error: Error) =>
            toast.error(`Exempt failed: ${error.message}`),
    });

    const mergeMutation = useMutation({
        mutationFn: (args: { keepId: string; deleteIds: string[]; }) =>
            splClient.mergeDuplicates(args.keepId, args.deleteIds),
        onSuccess: (response) => {
            toast.success(
                `Merged into ${
                    response.kept.slice(0, 8)
                }… and deleted ${response.deleted_count}`,
            );
            invalidate();
            setMergeTarget(null);
        },
        onError: (error: Error) =>
            toast.error(`Merge failed: ${error.message}`),
    });

    const mergeMembers = useMemo(() => {
        if (!mergeTarget) return [];
        const set = new Set(mergeTarget.selected);
        return mergeTarget.cluster.members.filter((member) =>
            set.has(member.id)
        );
    }, [mergeTarget]);

    if (clusters.length === 0) {
        return (
            <div className="p-12 text-center text-text-secondary bg-bg-secondary rounded-lg border border-border">
                <Copy size={40} className="mx-auto mb-3 text-sky-400" />
                <p className="text-lg font-medium">No duplicate clusters</p>
                <p className="text-sm">
                    All possible-duplicate flags have been resolved.
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {clusters.map((cluster) => (
                    <ClusterCard
                        key={cluster.id}
                        cluster={cluster}
                        onMerge={(c, selected) =>
                            setMergeTarget({ cluster: c, selected })}
                        onExempt={(_c, selected) =>
                            exemptMutation.mutate(selected)}
                        isMerging={mergeMutation.isPending}
                        isExempting={exemptMutation.isPending}
                    />
                ))}
            </div>

            <MergeDuplicatesModal
                isOpen={mergeTarget !== null}
                onClose={() => setMergeTarget(null)}
                onConfirm={(keepId, deleteIds) =>
                    mergeMutation.mutate({ keepId, deleteIds })}
                members={mergeMembers}
                isSaving={mergeMutation.isPending}
            />
        </>
    );
};
